import { useGetFertilizers } from '@practium/features/api';
import { useToast, type FormSelectOption } from '@practium/ui';
import { useMemo, useState } from 'react';
import { usePostAdviceCorrectionCalcularCalagem } from '../../api/hooks/recommendations';
import { CalagemSchema } from '../schemas/correction.schema';
import { useCorrectionStore } from '../store/correctionStore';
import type { CalagemFormData, CalagemResult, CorrectionStartResponse } from '../types';
import { useTabCalculation } from './useTabCalculation';

const formatCorretivoOptionLabel = (product: {
	name?: string;
	CaO?: number;
	MgO?: number;
	PRNT?: number;
}) => {
	const details = [
		product.CaO != null ? `CaO: ${product.CaO}` : null,
		product.MgO != null ? `MgO: ${product.MgO}` : null,
		product.PRNT != null ? `PRNT: ${product.PRNT}` : null,
	].filter(Boolean);

	return details.length > 0 ? `${product.name} (${details.join(', ')})` : product.name;
};

const numberString = (value: unknown): string => {
	if (value === null || value === undefined || value === '') return '';
	const numeric = Number(value);
	return Number.isFinite(numeric) ? String(numeric) : String(value);
};

const RESULT_START_KEYS: Record<string, string[]> = {
	Ca: ['Ca'],
	Mg: ['Mg'],
	'Sat. Ca': ['satCa'],
	'Sat. Mg': ['satMg'],
	'Sat. bases': ['satSB'],
	'pH água': ['pHagua'],
};

const getStartCurrent = (startResult: CorrectionStartResponse, param: string): string => {
	for (const key of RESULT_START_KEYS[param] ?? [param]) {
		const value = numberString(startResult.atual?.[key]);
		if (value) return value;
	}
	return '—';
};

const getStartRange = (startResult: CorrectionStartResponse, param: string): string => {
	for (const key of RESULT_START_KEYS[param] ?? [param]) {
		const value = startResult.faixaAdequada?.[key];
		if (value) return value;
	}
	return '—';
};

const applyStartResultToCalagemTable = (
	result: CalagemResult,
	startResult: CorrectionStartResponse
): CalagemResult => ({
	...result,
	table: result.table.map((row) => ({
		...row,
		current: getStartCurrent(startResult, row.param),
		range: getStartRange(startResult, row.param),
	})),
});

export const useCalagemTab = () => {
	const toast = useToast();
	const {
		advice,
		correctionStart,
		calagem,
		calagemAjuste,
		calagemResult,
		calagemHasCalculated,
		setCalagemField,
		setCalagemAjusteField,
		setCalagemResult,
		setCalagemHasCalculated,
		resetCalagem,
	} = useCorrectionStore();

	const { data: fertilizers = [] } = useGetFertilizers();

	const corretivoOptions = useMemo<FormSelectOption[]>(
		() =>
			fertilizers
				.filter((product) => product.category === 'lime')
				.map((product) => ({
					value: product._id,
					label: formatCorretivoOptionLabel(product) ?? product._id,
				})),
		[fertilizers]
	);

	const { mutateAsync: calcularCalagem } = usePostAdviceCorrectionCalcularCalagem();
	const [isAdjusting, setIsAdjusting] = useState(false);

	const calculateResult = async (form: CalagemFormData) => {
		const adviceId = advice?._id;
		if (!adviceId) {
			return Promise.reject(new Error('Recomendação não encontrada para calcular calagem.'));
		}

		const result = await calcularCalagem({
			adviceId,
			productId: form.corretivo,
			metodo: form.metodo,
			metodoValue: form.metodoValue,
			forma: form.forma,
			formaConfig: {
				p: Number(form.profundidade),
				l: Number(form.largura || 0),
				c: Number(form.comprimento || 0),
			},
		});

		return correctionStart ? applyStartResultToCalagemTable(result, correctionStart) : result;
	};

	const tabCalculation = useTabCalculation<CalagemFormData, CalagemResult>({
		form: calagem,
		ajuste: calagemAjuste,
		result: calagemResult,
		hasCalculated: calagemHasCalculated,
		setField: setCalagemField,
		setAjusteField: setCalagemAjusteField,
		setResult: setCalagemResult,
		setHasCalculated: setCalagemHasCalculated,
		reset: resetCalagem,
		schema: CalagemSchema,
		calculate: calculateResult,
		tabName: 'Calagem',
		onSetField: () => {
			setCalagemResult(null);
			setCalagemHasCalculated(false);
		},
	});

	const handleAdjustDose = async () => {
		const dose = Number(calagemAjuste.dose);
		if (!calagem.corretivo || !Number.isFinite(dose) || dose < 0) {
			toast.error('Informe uma dose válida para recalcular a calagem.');
			return;
		}

		setIsAdjusting(true);
		try {
			const result = await calculateResult(calagem);
			setCalagemResult(result);
			setCalagemAjusteField('dose', result.ajuste.dose);
			setCalagemAjusteField('doseSecundaria', result.ajuste.doseSecundaria);
		} catch (error) {
			toast.error(error instanceof Error ? error.message : 'Erro ao recalcular calagem.');
		} finally {
			setIsAdjusting(false);
		}
	};

	return {
		...tabCalculation,
		corretivoOptions,
		handleAdjustDose,
		isAdjusting,
	};
};
